import React, { useContext, useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    Divider,
    IconButton,
    Link,
    Stack,
    Tooltip,
    Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { BillType, IdBill, IDTransactionLog, TransactionStatus, TrxLogType } from 'realhaus-sdk';
import { RootStoreContext } from '../../global/storeContext';
import { IsSystemWallet } from '../../global/util';
import { paths } from '../../routes';
import { RightDrawer } from '../uiComponents/rightDrawer';
import {
    Activity,
    describeTransaction,
    DetailLink,
    explainTransaction,
    formatSignedAmount,
    getTransactionKind,
    isOffPlatformServiceFee,
    RelatedTransactions,
    TransactionKind,
    transactionDetailRows,
} from '../../utils/transaction';

/**
 * Load what a transaction refers to (its bill, lease and the other person) and the transactions
 * related to it, as far as the viewer may read them. Each lookup is best effort: one that fails
 * leaves its detail out instead of failing the view.
 */
const useTransactionContext = (activity: Activity) => {
    const { walletStore, leaseStore, userStore } = useContext(RootStoreContext);
    const [context, setContext] = useState<{ activity: Activity; related: RelatedTransactions }>({
        activity,
        related: {},
    });
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        let cancelled = false;
        const viewerId = userStore.userId ?? '';

        const safely = async <T,>(load: () => Promise<T | undefined>) => {
            try {
                return await load();
            } catch (err) {
                console.error(err);
                return undefined;
            }
        };

        const withReferences = async (
            trx: IDTransactionLog,
            known: Partial<Activity> = {},
        ): Promise<Activity> => {
            const { leaseId, billId } = trx.meta ?? {};
            const [lease, bill] = await Promise.all([
                known.lease ??
                    (leaseId ? safely(() => leaseStore.getLeaseAgreementById(leaseId)) : undefined),
                known.bill ??
                    (leaseId && billId
                        ? safely(async () => {
                              const b = await leaseStore.getLeaseBill(leaseId, billId);
                              return b ? ({ id: billId, ...b } as IdBill) : undefined;
                          })
                        : undefined),
            ]);
            const otherId = trx.senderId === viewerId ? trx.receiverId : trx.senderId;
            const counterparty =
                known.counterparty ??
                (otherId && otherId !== viewerId && !IsSystemWallet(otherId)
                    ? await safely(() => userStore.getUserProfile(otherId))
                    : undefined);
            return { trx, lease, bill, counterparty };
        };

        const load = async () => {
            setLoading(true);
            const full = await withReferences(activity.trx, activity);
            const { trx, bill } = full;
            const related: RelatedTransactions = {};

            switch (getTransactionKind(trx, viewerId)) {
                case TransactionKind.SERVICE_FEE: {
                    if (bill) {
                        related.billParty = await safely(() =>
                            userStore.getUserProfile(bill.debtorId),
                        );
                    }
                    const paymentId = trx.meta?.trxLogRef;
                    const payment = paymentId
                        ? await safely(() => walletStore.getTransactionLogWithId(paymentId))
                        : undefined;
                    if (payment?.type === TrxLogType.PAYMENT) {
                        // The payer is looked up from the payment: a co-tenant may pay the bill.
                        related.payment = await withReferences(payment, {
                            bill: full.bill,
                            lease: full.lease,
                        });
                    }
                    if (isOffPlatformServiceFee(trx)) {
                        related.payout = await safely(() =>
                            walletStore.findPayoutForTransaction(trx.id),
                        );
                    }
                    break;
                }
                case TransactionKind.BILL_PAYMENT_RECEIVED:
                    // Only rent carries a service fee.
                    if (!bill || bill.type === BillType.RENT) {
                        related.serviceFee = await safely(() =>
                            walletStore.findServiceFeeForPayment(trx.id),
                        );
                    }
                    break;
                case TransactionKind.PAYOUT: {
                    const fees = (await safely(() => walletStore.getFeesForPayout(trx))) ?? [];
                    related.fees = await Promise.all(fees.map((fee) => withReferences(fee)));
                    break;
                }
                case TransactionKind.REFUND: {
                    const paymentId = trx.meta?.trxLogRef;
                    const payment = paymentId
                        ? await safely(() => walletStore.getTransactionLogWithId(paymentId))
                        : undefined;
                    if (payment) related.payment = await withReferences(payment);
                    break;
                }
            }

            if (!cancelled) {
                setContext({ activity: full, related });
                setLoading(false);
            }
        };

        setContext({ activity, related: {} });
        load();
        return () => {
            cancelled = true;
        };
    }, [activity]);

    return { ...context, loading };
};

const ResendWithdrawInstructions: React.FC<{ trxLogId: string }> = observer(({ trxLogId }) => {
    const { walletStore, uiStore } = useContext(RootStoreContext);
    const [disabled, setDisabled] = useState(false);

    const resend = () => {
        uiStore.showLoading();
        walletStore
            .resendWithdrawalInstruction(trxLogId)
            .then((res) => {
                if (!res) return;
                uiStore.success('Withdrawal instruction sent');
                setDisabled(true);
                setTimeout(() => setDisabled(false), 60000);
            })
            .catch((err) => {
                console.error(err);
                uiStore.error('Failed to send withdrawal instruction');
            })
            .finally(() => uiStore.hideLoading());
    };

    return (
        <Box>
            <Button variant='contained' color='primary' disabled={disabled} onClick={resend}>
                Resend withdraw instructions
            </Button>
        </Box>
    );
});

/**
 * What a transaction was for, how its amount was worked out and what happens next. Related
 * transactions (the payment behind a fee, the payout a fee came out of) open in place.
 */
export const TransactionDetails: React.FC<{ activity: Activity }> = observer(({ activity }) => {
    const { userStore, uiStore } = useContext(RootStoreContext);
    const navigate = useNavigate();
    const [history, setHistory] = useState<Activity[]>([activity]);

    useEffect(() => {
        setHistory([activity]);
    }, [activity]);

    const current = history[history.length - 1];
    const viewerId = userStore.userId ?? '';
    const { activity: full, related, loading } = useTransactionContext(current);

    const description = describeTransaction(full, viewerId);
    const explanation = explainTransaction(full, related, viewerId);
    const rows = transactionDetailRows(full, related, viewerId);
    const trx = full.trx;

    const openLink = (link: DetailLink) => {
        if (link.kind === 'lease') {
            const leases = link.isOwner ? paths.landlord.tenants : paths.tenant.leases;
            navigate(`${leases}?leaseId=${link.leaseId}`);
            return;
        }
        setHistory([...history, link.activity]);
    };

    const copyReference = (reference: string) => {
        navigator.clipboard
            ?.writeText(reference)
            .then(() => uiStore.success('Reference copied'))
            .catch(() => undefined);
    };

    return (
        <Stack spacing={3}>
            {history.length > 1 && (
                <Box>
                    <Button
                        startIcon={<ArrowBackIcon />}
                        onClick={() => setHistory(history.slice(0, -1))}
                    >
                        Back
                    </Button>
                </Box>
            )}

            <Stack
                direction={{ xs: 'column', sm: 'row' }}
                justifyContent='space-between'
                alignItems={{ xs: 'flex-start', sm: 'flex-start' }}
                spacing={2}
            >
                <Box>
                    <Typography variant='h5' component='h2'>
                        {description.title}
                    </Typography>
                    <Typography variant='body2' color='text.secondary'>
                        {format(trx.timestamp, 'PPp')}
                    </Typography>
                </Box>
                <Stack alignItems={{ xs: 'flex-start', sm: 'flex-end' }} spacing={1}>
                    <Typography
                        variant='h5'
                        component='p'
                        sx={{
                            whiteSpace: 'nowrap',
                            color: description.direction === 'in' ? 'success.main' : 'text.primary',
                        }}
                    >
                        {formatSignedAmount(description.amount, description.direction)}
                    </Typography>
                    <Chip
                        size='small'
                        variant='outlined'
                        label={description.status.label}
                        color={description.status.tone}
                    />
                </Stack>
            </Stack>

            {explanation && <Typography variant='body1'>{explanation}</Typography>}

            {loading && (
                <Stack direction='row' spacing={1} alignItems='center'>
                    <CircularProgress size={16} />
                    <Typography variant='body2' color='text.secondary'>
                        Loading details…
                    </Typography>
                </Stack>
            )}

            <Box
                component='dl'
                sx={{ m: 0, border: 1, borderColor: 'divider', borderRadius: 1 }}
            >
                {rows.map((row, index) => (
                    <React.Fragment key={`${row.label}-${index}`}>
                        {index > 0 && <Divider />}
                        <Box
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: { xs: '1fr', sm: '200px 1fr' },
                                columnGap: 2,
                                rowGap: 0.5,
                                p: 2,
                            }}
                        >
                            <Typography component='dt' variant='body2' color='text.secondary'>
                                {row.label}
                            </Typography>
                            <Box component='dd' sx={{ m: 0 }}>
                                <Typography
                                    component='span'
                                    variant='body1'
                                    sx={{ wordBreak: 'break-word' }}
                                >
                                    {row.value}
                                </Typography>
                                {row.link && (
                                    <Link
                                        component='button'
                                        variant='body2'
                                        sx={{ ml: 1, verticalAlign: 'baseline' }}
                                        onClick={() => openLink(row.link!)}
                                    >
                                        {row.link.kind === 'lease' ? 'Open lease' : 'View'}
                                    </Link>
                                )}
                                {row.label === 'Reference' && (
                                    <Tooltip title='Copy reference'>
                                        <IconButton
                                            size='small'
                                            aria-label='Copy reference'
                                            onClick={() => copyReference(row.value)}
                                        >
                                            <ContentCopyIcon fontSize='inherit' />
                                        </IconButton>
                                    </Tooltip>
                                )}
                            </Box>
                        </Box>
                    </React.Fragment>
                ))}
            </Box>

            {trx.type === TrxLogType.WITHDRAW && trx.status === TransactionStatus.PROCESSING && (
                <ResendWithdrawInstructions trxLogId={trx.id} />
            )}

            {description.kind === TransactionKind.SERVICE_FEE && (
                <Typography variant='body2' color='text.secondary'>
                    Realhaus service fees are set out in the{' '}
                    <Link href={paths.termsofservice} target='_blank' rel='noopener'>
                        Terms of Service
                    </Link>
                    .
                </Typography>
            )}
        </Stack>
    );
});

/** The transaction details in the app's right-hand drawer. Closed when there is no activity. */
export const TransactionDetailsDrawer: React.FC<{ activity?: Activity; onClose: () => void }> =
    observer(({ activity, onClose }) => {
        // Keep showing the last transaction while the drawer slides closed.
        const [shown, setShown] = useState<Activity | undefined>(activity);
        useEffect(() => {
            if (activity) setShown(activity);
        }, [activity]);

        return (
            <RightDrawer isOpen={!!activity} onOpen={() => undefined} onClose={onClose}>
                {shown && <TransactionDetails activity={shown} />}
            </RightDrawer>
        );
    });
